import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import authService from "../../services/authService";
import paymentService from "../../services/paymentService";
import {
  setTokens,
  clearTokens,
  hasTokens,
  REFRESH_TOKEN_KEY,
} from "../../utils/auth";
import logger from "../../utils/logger";

// ==================== ASYNC THUNKS ====================

/**
 * Login user
 */
export const login = createAsyncThunk(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const { user, tokens } = await authService.login(credentials);

      // Store tokens in localStorage
      setTokens(tokens.access.token, tokens.refresh.token);

      return user;
    } catch (error) {
      const message = error.response?.data?.message || "Login failed";
      return rejectWithValue(message);
    }
  },
);

/**
 * Initiate registration - Step 1: Send verification code to email
 */
export const initiateRegistration = createAsyncThunk(
  "auth/initiateRegistration",
  async (email, { rejectWithValue }) => {
    try {
      const response = await authService.initiateRegistration(email);
      return { email, message: response.message };
    } catch (error) {
      const message = error.response?.data?.message || "Registration failed";
      return rejectWithValue(message);
    }
  },
);

/**
 * Complete registration - Step 2: Verify code and create user
 */
export const completeRegistration = createAsyncThunk(
  "auth/completeRegistration",
  async (userData, { rejectWithValue }) => {
    try {
      const { user, tokens } = await authService.verifyRegistration(userData);

      // Store tokens in localStorage
      setTokens(tokens.access.token, tokens.refresh.token);

      return user;
    } catch (error) {
      const message =
        error.response?.data?.message || "Registration verification failed";
      return rejectWithValue(message);
    }
  },
);

/**
 * Resend registration verification code
 */
export const resendRegistrationCode = createAsyncThunk(
  "auth/resendRegistrationCode",
  async (email, { rejectWithValue }) => {
    try {
      await authService.resendRegistrationVerification(email);
      return true;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to resend code";
      return rejectWithValue(message);
    }
  },
);

/**
 * Logout user
 */
export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { getState, rejectWithValue }) => {
    try {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

      if (refreshToken) {
        await authService.logout(refreshToken);
      }

      // Clear tokens from localStorage
      clearTokens();

      return null;
    } catch (error) {
      // Even if API call fails, clear local tokens
      clearTokens();
      return null;
    }
  },
);

/**
 * Get current user (for initializing auth state on app load)
 */
export const getCurrentUser = createAsyncThunk(
  "auth/getCurrentUser",
  async (_, { rejectWithValue }) => {
    try {
      // Check if tokens exist
      if (!hasTokens()) {
        return rejectWithValue("No tokens found");
      }

      const user = await authService.getCurrentUser();
      return user;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to get user";
      clearTokens(); // Clear invalid tokens
      return rejectWithValue(message);
    }
  },
);

/**
 * Refresh user data silently (doesn't trigger global loading state)
 */
export const refreshUser = createAsyncThunk(
  "auth/refreshUser",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getCurrentUser();
      return response;
    } catch (error) {
      // We don't want to logout on background refresh errors usually,
      // but we pass the error along just in case.
      return rejectWithValue(error.response?.data);
    }
  },
);

/**
 * Request password reset
 */
export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (email, { rejectWithValue }) => {
    try {
      await authService.forgotPassword(email);
      return email;
    } catch (error) {
      const message = error.response?.data?.message || "Request failed";
      return rejectWithValue(message);
    }
  },
);

/**
 * Reset password
 */
export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async ({ email, code, password }, { rejectWithValue }) => {
    try {
      const response = await authService.resetPassword({
        email,
        code,
        password,
      });
      return response;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to reset password";
      return rejectWithValue(message);
    }
  },
);

/**
 * Update user profile
 */
export const updateProfile = createAsyncThunk(
  "auth/updateProfile",
  async (data, { rejectWithValue }) => {
    try {
      const response = await authService.updateProfile(data);
      return response;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update profile";
      logger.error("Error updating profile:", error);
      return rejectWithValue(message);
    }
  },
);

/**
 * Update user password (when logged in)
 */
export const updatePassword = createAsyncThunk(
  "auth/updatePassword",
  async (data, { rejectWithValue }) => {
    try {
      await authService.updatePassword(data);
      return true;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update password";
      logger.error("Error updating password:", error);
      return rejectWithValue(message);
    }
  },
);

/**
 * Send verification email
 */
export const sendVerificationEmail = createAsyncThunk(
  "auth/sendVerificationEmail",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.sendVerificationEmail();
      return response;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to send verification email";
      logger.error("Error sending verification email:", error);
      return rejectWithValue(message);
    }
  },
);

/**
 * Verify email with code
 */
export const verifyEmail = createAsyncThunk(
  "auth/verifyEmail",
  async (code, { rejectWithValue }) => {
    try {
      await authService.verifyEmail(code);
      return true;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to verify email";
      logger.error("Error verifying email:", error);
      return rejectWithValue(message);
    }
  },
);

/**
 * Send phone verification code
 */
export const sendPhoneVerification = createAsyncThunk(
  "auth/sendPhoneVerification",
  async (_, { rejectWithValue }) => {
    try {
      await authService.sendPhoneVerification();
      return true;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to send verification code";
      logger.error("Error sending phone verification:", error);
      return rejectWithValue(message);
    }
  },
);

/**
 * Verify phone number
 */
export const verifyPhone = createAsyncThunk(
  "auth/verifyPhone",
  async (code, { rejectWithValue }) => {
    try {
      await authService.verifyPhone(code);
      return true;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to verify phone number";
      logger.error("Error verifying phone:", error);
      return rejectWithValue(message);
    }
  },
);

/**
 * Initialize auth state on app load
 * Checks for tokens and fetches current user if they exist
 */
export const initializeAuth = createAsyncThunk(
  "auth/initialize",
  async (_, { rejectWithValue }) => {
    try {
      // Check if tokens exist in localStorage
      if (!hasTokens()) {
        logger.debug("No tokens found - user not authenticated");
        return null;
      }

      logger.debug("Tokens found - fetching user profile");

      // Tokens exist - fetch current user
      // Note: If access token is expired, the axios interceptor will:
      // 1. Detect expiration
      // 2. Refresh the token automatically
      // 3. Retry the request
      const user = await authService.getCurrentUser();

      logger.debug("Auth initialized successfully");
      return user;
    } catch (error) {
      // This only happens if BOTH tokens are invalid/expired
      // The axios interceptor already tried to refresh
      logger.warn("Auth initialization failed - tokens invalid", error);

      clearTokens();
      return rejectWithValue("Session expired");
    }
  },
);

/**
 * Fetch payment methods
 */
export const fetchPaymentMethods = createAsyncThunk(
  "auth/fetchPaymentMethods",
  async (_, { rejectWithValue }) => {
    try {
      const paymentMethods = await paymentService.getPaymentMethods();
      return paymentMethods; // Array of payment methods
    } catch (error) {
      logger.error("Error fetching payment methods", error);
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch payment methods",
      );
    }
  },
);

/**
 * Add payment method
 */
export const addPaymentMethod = createAsyncThunk(
  "auth/addPaymentMethod",
  async (paymentMethodId, { rejectWithValue }) => {
    try {
      const updatedPaymentMethods =
        await paymentService.addPaymentMethod(paymentMethodId);
      return updatedPaymentMethods; // Return updated array of payment methods
    } catch (error) {
      logger.error("Error adding payment method", error);
      return rejectWithValue(
        error.response?.data?.message || "Failed to add payment method",
      );
    }
  },
);

/**
 * Delete payment method (for future use)
 */
export const deletePaymentMethod = createAsyncThunk(
  "auth/deletePaymentMethod",
  async (paymentMethodId, { rejectWithValue }) => {
    try {
      await paymentService.deletePaymentMethod(paymentMethodId);
      return paymentMethodId; // Return ID to remove from state
    } catch (error) {
      logger.error("Error deleting payment method", error);
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete payment method",
      );
    }
  },
);

/**
 * Set default payment method
 */
export const setDefaultPaymentMethod = createAsyncThunk(
  "auth/setDefaultPaymentMethod",
  async ({ paymentMethodId }, { rejectWithValue, dispatch }) => {
    try {
      await paymentService.setDefaultPaymentMethod(paymentMethodId);
      // Refetch to get updated default
      await dispatch(fetchPaymentMethods());
      return paymentMethodId;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to set default payment method";
      return rejectWithValue(message);
    }
  },
);

/**
 * Upgrade homeowner to service provider
 */
export const upgradeToProvider = createAsyncThunk(
  "auth/upgradeToProvider",
  async (data, { rejectWithValue }) => {
    try {
      const response = await authService.upgradeToProvider(data);
      return response;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to upgrade to provider";
      logger.error("Error upgrading to provider:", error);
      return rejectWithValue(message);
    }
  },
);

/**
 * Unlink OAuth provider from account
 */
export const unlinkOAuth = createAsyncThunk(
  "auth/unlinkOAuth",
  async (provider, { rejectWithValue }) => {
    try {
      await authService.unlinkOAuth(provider);
      return provider;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to unlink account";
      logger.error("Error unlinking OAuth:", error);
      return rejectWithValue(message);
    }
  },
);

// ==================== EMAIL CHANGE THUNKS ====================

/**
 * Initiate email change - sends verification code to new email
 */
export const initiateEmailChange = createAsyncThunk(
  "auth/initiateEmailChange",
  async (email, { rejectWithValue }) => {
    try {
      const response = await authService.initiateEmailChange(email);
      return { email, message: response.message };
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to initiate email change";
      logger.error("Error initiating email change:", error);
      return rejectWithValue(message);
    }
  },
);

/**
 * Verify email change with code
 */
export const verifyEmailChange = createAsyncThunk(
  "auth/verifyEmailChange",
  async (code, { rejectWithValue }) => {
    try {
      const response = await authService.verifyEmailChange(code);
      return response; // Returns updated user profile
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to verify email change";
      logger.error("Error verifying email change:", error);
      return rejectWithValue(message);
    }
  },
);

/**
 * Resend email change verification code
 */
export const resendEmailChangeCode = createAsyncThunk(
  "auth/resendEmailChangeCode",
  async (_, { rejectWithValue }) => {
    try {
      await authService.resendEmailChangeCode();
      return true;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to resend code";
      logger.error("Error resending email change code:", error);
      return rejectWithValue(message);
    }
  },
);

/**
 * Cancel pending email change
 */
export const cancelEmailChange = createAsyncThunk(
  "auth/cancelEmailChange",
  async (_, { rejectWithValue }) => {
    try {
      await authService.cancelEmailChange();
      return true;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to cancel email change";
      logger.error("Error canceling email change:", error);
      return rejectWithValue(message);
    }
  },
);

// ==================== PHONE CHANGE THUNKS ====================

/**
 * Initiate phone change - sends verification code via SMS
 */
export const initiatePhoneChange = createAsyncThunk(
  "auth/initiatePhoneChange",
  async (phone, { rejectWithValue }) => {
    try {
      const response = await authService.initiatePhoneChange(phone);
      return { phone, message: response.message };
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to initiate phone change";
      logger.error("Error initiating phone change:", error);
      return rejectWithValue(message);
    }
  },
);

/**
 * Verify phone change with code
 */
export const verifyPhoneChange = createAsyncThunk(
  "auth/verifyPhoneChange",
  async (code, { rejectWithValue }) => {
    try {
      const response = await authService.verifyPhoneChange(code);
      return response; // Returns updated user profile
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to verify phone change";
      logger.error("Error verifying phone change:", error);
      return rejectWithValue(message);
    }
  },
);

/**
 * Resend phone change verification code
 */
export const resendPhoneChangeCode = createAsyncThunk(
  "auth/resendPhoneChangeCode",
  async (_, { rejectWithValue }) => {
    try {
      await authService.resendPhoneChangeCode();
      return true;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to resend code";
      logger.error("Error resending phone change code:", error);
      return rejectWithValue(message);
    }
  },
);

/**
 * Cancel pending phone change
 */
export const cancelPhoneChange = createAsyncThunk(
  "auth/cancelPhoneChange",
  async (_, { rejectWithValue }) => {
    try {
      await authService.cancelPhoneChange();
      return true;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to cancel phone change";
      logger.error("Error canceling phone change:", error);
      return rejectWithValue(message);
    }
  },
);

// ==================== SLICE ====================

const initialState = {
  user: null,
  isAuthenticated: false,
  loading: true, // Start in loading state until initialized
  error: null,
  // Registration flow (2-step)
  registrationEmail: null, // Email being verified during signup
  registrationStep: "form", // 'form' | 'verify'
  registrationLoading: false,
  // Forgot password flow
  passwordResetEmail: null, // Store email for forgot password success message
  passwordResetSuccess: false, // Flag for password reset success
  // Update password flow (when logged in)
  passwordUpdateLoading: false,
  // User profile update
  profileUpdateLoading: false,
  // Payment methods
  paymentMethods: [],
  paymentMethodsLoading: false,
  paymentMethodsError: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Clear payment methods error
    clearPaymentMethodsError: (state) => {
      state.paymentMethodsError = null;
    },

    // Clear password reset state
    clearPasswordReset: (state) => {
      state.passwordResetEmail = null;
      state.passwordResetSuccess = false;
    },

    // Clear registration state (for going back in signup flow)
    clearRegistration: (state) => {
      state.registrationEmail = null;
      state.registrationStep = "form";
      state.registrationLoading = false;
      state.error = null;
    },

    // Manual logout (without API call - used by interceptor)
    clearAuth: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      clearTokens();
    },

    // Set auth state from OAuth callback
    setAuthFromOAuth: (state, action) => {
      const { accessToken, refreshToken, user } = action.payload;

      // Store tokens in localStorage
      setTokens(accessToken, refreshToken);

      // Update state
      state.user = user;
      state.isAuthenticated = true;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
      })

      // Initiate Registration (Step 1)
      .addCase(initiateRegistration.pending, (state) => {
        state.registrationLoading = true;
        state.error = null;
      })
      .addCase(initiateRegistration.fulfilled, (state, action) => {
        state.registrationLoading = false;
        state.registrationEmail = action.payload.email;
        state.registrationStep = "verify";
        state.error = null;
      })
      .addCase(initiateRegistration.rejected, (state, action) => {
        state.registrationLoading = false;
        state.error = action.payload;
      })

      // Complete Registration (Step 2)
      .addCase(completeRegistration.pending, (state) => {
        state.registrationLoading = true;
        state.error = null;
      })
      .addCase(completeRegistration.fulfilled, (state, action) => {
        state.registrationLoading = false;
        state.registrationEmail = null;
        state.registrationStep = "form";
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(completeRegistration.rejected, (state, action) => {
        state.registrationLoading = false;
        state.error = action.payload;
      })

      // Resend Registration Code
      .addCase(resendRegistrationCode.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Logout
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
        state.paymentMethods = [];
        state.paymentMethodsError = null;
      })

      // Get Current User
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(getCurrentUser.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
      });

    // Refresh User - Only update data, do NOT touch loading state
    builder
      .addCase(refreshUser.fulfilled, (state, action) => {
        state.user = action.payload;
      })

      // Update profile
      .addCase(updateProfile.pending, (state) => {
        state.profileUpdateLoading = true;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.profileUpdateLoading = false;
        // Update user data in state
        state.user = {
          ...state.user,
          ...action.payload,
        };
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.profileUpdateLoading = false;
      })

      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.passwordResetEmail = action.meta.arg; // Store email
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.passwordResetSuccess = true;
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Update Password
      .addCase(updatePassword.pending, (state) => {
        state.passwordUpdateLoading = true;
      })
      .addCase(updatePassword.fulfilled, (state) => {
        state.passwordUpdateLoading = false;
      })
      .addCase(updatePassword.rejected, (state, action) => {
        state.passwordUpdateLoading = false;
      })

      // Initialize Auth
      .addCase(initializeAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.user = action.payload;
          state.isAuthenticated = true;
        } else {
          state.user = null;
          state.isAuthenticated = false;
        }
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
      });

    // Fetch payment methods
    builder
      .addCase(fetchPaymentMethods.pending, (state) => {
        state.paymentMethodsLoading = true;
        state.paymentMethodsError = null;
      })
      .addCase(fetchPaymentMethods.fulfilled, (state, action) => {
        state.paymentMethodsLoading = false;
        state.paymentMethods = action.payload;
      })
      .addCase(fetchPaymentMethods.rejected, (state, action) => {
        state.paymentMethodsLoading = false;
        state.paymentMethodsError = action.payload;
      });

    // Add payment method
    builder
      .addCase(addPaymentMethod.pending, (state) => {
        state.paymentMethodsLoading = true;
        state.paymentMethodsError = null;
      })
      .addCase(addPaymentMethod.fulfilled, (state, action) => {
        state.paymentMethodsLoading = false;
        state.paymentMethods = action.payload; // Backend returns updated list
      })
      .addCase(addPaymentMethod.rejected, (state, action) => {
        state.paymentMethodsLoading = false;
        state.paymentMethodsError = action.payload;
      });

    // Delete payment method
    builder.addCase(deletePaymentMethod.fulfilled, (state, action) => {
      state.paymentMethods = state.paymentMethods.filter(
        (pm) => pm.id !== action.payload,
      );
    });

    // Upgrade to provider
    builder
      .addCase(upgradeToProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(upgradeToProvider.fulfilled, (state, action) => {
        state.loading = false;
        // Update user role to service_provider
        if (state.user) {
          state.user = {
            ...state.user,
            role: "service_provider",
            businessName: action.payload.businessName,
            ...(Object.prototype.hasOwnProperty.call(action.payload, "phone")
              ? { phone: action.payload.phone }
              : {}),
          };
        }
      })
      .addCase(upgradeToProvider.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Verify Phone
    builder.addCase(verifyPhone.fulfilled, (state) => {
      if (state.user) {
        state.user.isPhoneVerified = true;
      }
    });

    // Verify Email
    builder.addCase(verifyEmail.fulfilled, (state) => {
      if (state.user) {
        state.user.isEmailVerified = true;
      }
    });

    // Unlink OAuth
    builder.addCase(unlinkOAuth.fulfilled, (state, action) => {
      if (state.user && state.user.linkedOAuthProviders) {
        state.user.linkedOAuthProviders =
          state.user.linkedOAuthProviders.filter(
            (provider) => provider !== action.payload,
          );
      }
    });

    // Email Change - Verify success updates user
    builder.addCase(verifyEmailChange.fulfilled, (state, action) => {
      if (state.user) {
        state.user = {
          ...state.user,
          ...action.payload,
          pendingEmail: null,
        };
      }
    });

    // Email Change - Cancel clears pending
    builder.addCase(cancelEmailChange.fulfilled, (state) => {
      if (state.user) {
        state.user.pendingEmail = null;
      }
    });

    // Phone Change - Verify success updates user
    builder.addCase(verifyPhoneChange.fulfilled, (state, action) => {
      if (state.user) {
        state.user = {
          ...state.user,
          ...action.payload,
          pendingPhone: null,
        };
      }
    });

    // Phone Change - Cancel clears pending
    builder.addCase(cancelPhoneChange.fulfilled, (state) => {
      if (state.user) {
        state.user.pendingPhone = null;
      }
    });
  },
});

// ==================== SELECTORS ====================

export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectUserRole = (state) => state.auth.user?.role;
export const selectIsHomeowner = (state) =>
  state.auth.user?.role === "homeowner";
export const selectIsProvider = (state) =>
  state.auth.user?.role === "service_provider";
export const selectPasswordResetEmail = (state) =>
  state.auth.passwordResetEmail;
export const selectPasswordResetSuccess = (state) =>
  state.auth.passwordResetSuccess;
export const selectPasswordUpdateLoading = (state) =>
  state.auth.passwordUpdateLoading;
export const selectPaymentMethods = (state) => state.auth.paymentMethods;
export const selectDefaultPaymentMethod = (state) =>
  state.auth.paymentMethods.find((pm) => pm.isDefault) || null;
export const selectHasPaymentMethod = (state) =>
  state.auth.paymentMethods.length > 0;
export const selectPaymentMethodsLoading = (state) =>
  state.auth.paymentMethodsLoading;
export const selectPaymentMethodsError = (state) =>
  state.auth.paymentMethodsError;

// Registration selectors
export const selectRegistrationEmail = (state) => state.auth.registrationEmail;
export const selectRegistrationStep = (state) => state.auth.registrationStep;
export const selectRegistrationLoading = (state) =>
  state.auth.registrationLoading;

// Pending verification selectors
export const selectPendingEmail = (state) =>
  state.auth.user?.pendingEmail || null;
export const selectPendingPhone = (state) =>
  state.auth.user?.pendingPhone || null;

// OAuth selectors
export const selectLinkedOAuthProviders = (state) =>
  state.auth.user?.linkedOAuthProviders || [];
export const selectHasPassword = (state) =>
  state.auth.user?.hasPassword ?? true; // Default to true for backwards compatibility
export const selectIsGoogleLinked = (state) =>
  state.auth.user?.linkedOAuthProviders?.includes("google") || false;

// ==================== EXPORTS ====================

export const {
  clearError,
  clearPasswordReset,
  clearRegistration,
  clearAuth,
  clearPaymentMethodsError,
  setAuthFromOAuth,
} = authSlice.actions;
export default authSlice.reducer;
